import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { aiRecommendationsAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface RecommendationStep {
  title: string;
  description: string;
  completed: boolean;
  estimatedTime: string;
}

export interface PostExample {
  type: string;
  title: string;
  description: string;
  caption: string;
  hashtags: string[];
  optimalTime: string;
  expectedEngagement: string;
}

export interface RecommendationMetrics {
  currentEngagement: number;
  targetEngagement: number;
  currentReach: number;
  targetReach: number;
  currentFollowers: number;
  targetFollowers: number;
}

export interface RecommendationDetail {
  id: string;
  platform: string;
  priority: string;
  title: string;
  description: string;
  impact: string;
  effort: string;
  timeline: string;
  status: string;
  icon: string;
  color: string;
  bgColor: string;
  category: string;
  steps: RecommendationStep[];
  tips: string[];
  postExamples: PostExample[];
  metrics: RecommendationMetrics;
}

export interface BasicRecommendation {
  title: string;
  description: string;
}

export interface Recommendation {
  id: string;
  platform: string;
  priority: string;
  title: string;
  description: string;
  impact: string;
  effort: string;
  timeline: string;
  status: string;
  icon: string;
  color: string;
  bgColor: string;
  category: string;
}

export interface RecommendationCategory {
  title: string;
  description: string;
  recommendations: Recommendation[];
  basic?: BasicRecommendation[];
}

export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning';
  title: string;
  description: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  [key: string]: AIInsight[];
}

export interface AIRecommendationsState {
  recommendations: { [key: string]: RecommendationCategory } | null;
  recommendationDetail: RecommendationDetail | null;
  charts: {
    [key: string]: ChartData | null;
  };
  aiAnalysis: AIAnalysis;
  loading: {
    recommendations: boolean;
    recommendationDetail: boolean;
    charts: boolean;
    aiAnalysis: boolean;
  };
  error: {
    recommendations: string | null;
    recommendationDetail: string | null;
    charts: string | null;
    aiAnalysis: string | null;
  };
}

export type AIRecommendationsAction =
  | { type: 'FETCH_RECOMMENDATIONS_START' }
  | { type: 'FETCH_RECOMMENDATIONS_SUCCESS'; payload: { [key: string]: RecommendationCategory } }
  | { type: 'FETCH_RECOMMENDATIONS_ERROR'; payload: string }
  | { type: 'FETCH_RECOMMENDATION_DETAIL_START' }
  | { type: 'FETCH_RECOMMENDATION_DETAIL_SUCCESS'; payload: RecommendationDetail }
  | { type: 'FETCH_RECOMMENDATION_DETAIL_ERROR'; payload: string }
  | { type: 'FETCH_CHART_DATA_START'; payload: string }
  | { type: 'FETCH_CHART_DATA_SUCCESS'; payload: { chartType: string; data: ChartData } }
  | { type: 'FETCH_CHART_DATA_ERROR'; payload: { chartType: string; error: string } }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIInsight[] } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

// Initial state
const initialState: AIRecommendationsState = {
  recommendations: null,
  recommendationDetail: null,
  charts: {},
  aiAnalysis: {},
  loading: {
    recommendations: false,
    recommendationDetail: false,
    charts: false,
    aiAnalysis: false,
  },
  error: {
    recommendations: null,
    recommendationDetail: null,
    charts: null,
    aiAnalysis: null,
  },
};

// Reducer
const aiRecommendationsReducer = (state: AIRecommendationsState, action: AIRecommendationsAction): AIRecommendationsState => {
  switch (action.type) {
    case 'FETCH_RECOMMENDATIONS_START':
      return {
        ...state,
        loading: { ...state.loading, recommendations: true },
        error: { ...state.error, recommendations: null },
      };
    case 'FETCH_RECOMMENDATIONS_SUCCESS':
      return {
        ...state,
        recommendations: action.payload,
        loading: { ...state.loading, recommendations: false },
      };
    case 'FETCH_RECOMMENDATIONS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, recommendations: false },
        error: { ...state.error, recommendations: action.payload },
      };
    case 'FETCH_RECOMMENDATION_DETAIL_START':
      return {
        ...state,
        loading: { ...state.loading, recommendationDetail: true },
        error: { ...state.error, recommendationDetail: null },
      };
    case 'FETCH_RECOMMENDATION_DETAIL_SUCCESS':
      return {
        ...state,
        recommendationDetail: action.payload,
        loading: { ...state.loading, recommendationDetail: false },
      };
    case 'FETCH_RECOMMENDATION_DETAIL_ERROR':
      return {
        ...state,
        loading: { ...state.loading, recommendationDetail: false },
        error: { ...state.error, recommendationDetail: action.payload },
      };
    case 'FETCH_CHART_DATA_START':
      return {
        ...state,
        loading: { ...state.loading, charts: true },
        error: { ...state.error, charts: null },
      };
    case 'FETCH_CHART_DATA_SUCCESS':
      return {
        ...state,
        charts: { ...state.charts, [action.payload.chartType]: action.payload.data },
        loading: { ...state.loading, charts: false },
      };
    case 'FETCH_CHART_DATA_ERROR':
      return {
        ...state,
        loading: { ...state.loading, charts: false },
        error: { ...state.error, charts: action.payload.error },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: { ...state.loading, aiAnalysis: true },
        error: { ...state.error, aiAnalysis: null },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: { ...state.aiAnalysis, [action.payload.chartType]: action.payload.data },
        loading: { ...state.loading, aiAnalysis: false },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, aiAnalysis: false },
        error: { ...state.error, aiAnalysis: action.payload.error },
      };
    default:
      return state;
  }
};

// Context
const AIRecommendationsContext = createContext<{
  state: AIRecommendationsState;
  fetchRecommendations: () => Promise<void>;
  fetchRecommendationDetail: (id: string) => Promise<void>;
  setRecommendationDetail: (recommendation: any) => void;
  fetchChartData: (chartType: string) => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
} | undefined>(undefined);

// Provider
export const AIRecommendationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(aiRecommendationsReducer, initialState);
  const { business } = useAuth();

  const fetchRecommendations = useCallback(async () => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_RECOMMENDATIONS_START' });
      const response = await aiRecommendationsAPI.getRecommendations(business._id);
      dispatch({ type: 'FETCH_RECOMMENDATIONS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_RECOMMENDATIONS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_RECOMMENDATIONS_ERROR', payload: 'Öneriler yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchRecommendationDetail = useCallback(async (id: string) => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_RECOMMENDATION_DETAIL_START' });
      const response = await aiRecommendationsAPI.getRecommendationDetail(business._id, id);
      dispatch({ type: 'FETCH_RECOMMENDATION_DETAIL_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_RECOMMENDATION_DETAIL_ERROR', payload: 'Öneri detayı yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  // Recommendation objesini direkt state'e kaydetmek için (artık API çağrısı yok)
  const setRecommendationDetail = useCallback((recommendation: any) => {
    dispatch({ type: 'FETCH_RECOMMENDATION_DETAIL_SUCCESS', payload: recommendation });
  }, []);

  const fetchChartData = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_CHART_DATA_START', payload: chartType });
      const response = await aiRecommendationsAPI.getChartData(business._id, chartType);
      dispatch({ type: 'FETCH_CHART_DATA_SUCCESS', payload: { chartType, data: response.data.data } });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_CHART_DATA_ERROR', payload: { chartType, error: 'PREPARING' } });
      } else {
        dispatch({ type: 'FETCH_CHART_DATA_ERROR', payload: { chartType, error: 'Grafik verisi yüklenirken hata oluştu' } });
      }
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    try {
      dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
      const response = await aiRecommendationsAPI.getAIAnalysis(business._id, chartType);
      dispatch({ type: 'FETCH_AI_ANALYSIS_SUCCESS', payload: { chartType, data: response.data.data } });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_AI_ANALYSIS_ERROR', payload: { chartType, error: 'PREPARING' } });
      } else {
        dispatch({ type: 'FETCH_AI_ANALYSIS_ERROR', payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' } });
      }
    }
  }, [business?._id]);

  return (
    <AIRecommendationsContext.Provider
      value={{
        state,
        fetchRecommendations,
        fetchRecommendationDetail,
        setRecommendationDetail,
        fetchChartData,
        fetchAIAnalysis,
      }}
    >
      {children}
    </AIRecommendationsContext.Provider>
  );
};

// Hook
export const useAIRecommendations = () => {
  const context = useContext(AIRecommendationsContext);
  if (context === undefined) {
    throw new Error('useAIRecommendations must be used within an AIRecommendationsProvider');
  }
  return context;
};
